import { DateTime } from 'luxon';

import { getPreviousMonthYear, type MonthAndYear, type MonthYearPeriod } from ':helpers/date';
import { MONTH_DAY_TO_FETCH_KEYWORD_SEARCH_IMPRESSIONS } from ':modules/keyword-search-impressions/constants';

/**
 * Excludes months in the unavailable data range from the given month-year period.
 * Google data is only available from the 5th of the month for the previous month.
 *
 * @param {MonthYearPeriod} monthYearPeriod - The month-year period to be adjusted.
 * @returns {MonthYearPeriod} - The adjusted month-year period with excluded months.
 */
export function excludeMonthsInUnavailableDataRangeFromPeriod(monthYearPeriod: MonthYearPeriod): MonthYearPeriod {
    const ONE_MONTH = 1;
    const TWO_MONTHS = 2;
    const { endMonthYear } = monthYearPeriod;
    const currentMonthYear = { month: DateTime.now().month, year: DateTime.now().year };
    const previousMonthYear = getPreviousMonthYear(currentMonthYear, 1);

    const _isSameMonthYear = (a: MonthAndYear, b: MonthAndYear): boolean => a.year === b.year && a.month === b.month;
    const isCurrentDayLessThanThreshold = DateTime.now().day < MONTH_DAY_TO_FETCH_KEYWORD_SEARCH_IMPRESSIONS;

    if (_isSameMonthYear(endMonthYear, previousMonthYear)) {
        if (isCurrentDayLessThanThreshold) {
            return { ...monthYearPeriod, endMonthYear: getPreviousMonthYear(monthYearPeriod.endMonthYear, 1) };
        }
        return monthYearPeriod;
    }

    if (_isSameMonthYear(endMonthYear, currentMonthYear)) {
        const monthsToExclude = isCurrentDayLessThanThreshold ? TWO_MONTHS : ONE_MONTH;
        return {
            ...monthYearPeriod,
            endMonthYear: getPreviousMonthYear(monthYearPeriod.endMonthYear, monthsToExclude),
        };
    }

    return monthYearPeriod;
}
